/**
 * Centralized URL configuration
 * Logic layer that consumes data from src/data/productsConfig.js
 */
import { productConfig } from '../data/productsConfig';

// Current product - can be overridden by environment variable
export const CURRENT_PRODUCT = process.env.REACT_APP_PRODUCT || productConfig.defaultProduct;

// Products configuration
export const PRODUCTS = productConfig.products;

// Base URL for the application
export const BASE_URL = process.env.REACT_APP_BASE_URL || productConfig.baseUrl;

// Common path pattern (e.g. /01/:gtin/21/)
// This is used by React Router in App.js to match dynamic routes
export const COMMON_PATH = process.env.REACT_APP_PATH || productConfig.path;

// Resolved Base path for the CURRENT active product
// e.g. /01/0195949123456/21/F2L9Q3X7N6Y8/
export const BASE_PATH = (() => {
  const product = PRODUCTS[CURRENT_PRODUCT];
  const gtin = product?.gtin || '00000000000000';
  const serial = product?.serialNumber || '000000';
  return `/01/${gtin}/21/${serial}/`;
})();

// Route paths
export const ROUTES = productConfig.routes;

/**
 * Get full product path (path + serialNumber + /)
 * @param {string} productId - Product identifier (optional, defaults to current)
 * @returns {string} - Full product path with correct GTIN
 */
export const getProductPath = (productId = CURRENT_PRODUCT) => {
  const product = PRODUCTS[productId];
  if (!product) {
    console.warn(`Product '${productId}' not found`);
    return '/';
  }
  // Construct path dynamically using the product's GTIN and Serial
  return `/01/${product.gtin}/21/${product.serialNumber}/`;
};

/**
 * Get current product configuration
 * @returns {object} - Current product configuration object
 */
export const getCurrentProduct = () => {
  return PRODUCTS[CURRENT_PRODUCT];
};

/**
 * Get product data folder path
 * @param {string} productId - Product identifier (optional, defaults to current)
 * @returns {string} - Product data folder name
 */
export const getProductDataFolder = (productId = CURRENT_PRODUCT) => {
  return PRODUCTS[productId]?.dataFolder || 'linqOne';
};

/**
 * Helper function to get full route path
 * @param {string} routeName - Name of the route (e.g., 'onboard', 'dpp')
 * @returns {string} - Full path including base path
 */
export const getRoutePath = (routeName) => {
  const route = ROUTES[routeName];
  if (!route) {
    console.warn(`Route '${routeName}' not found in configuration`);
    return BASE_PATH;
  }
  return `${BASE_PATH}${route}`;
};

/**
 * Helper function to get full URL
 * @param {string} routeName - Name of the route (optional)
 * @returns {string} - Full URL including base URL and path
 */
export const getFullUrl = (routeName = null) => {
  const path = routeName ? getRoutePath(routeName) : BASE_PATH;
  return `${BASE_URL}${path}`;
};

/**
 * Detect product from scanned URL or path
 * @param {string} urlOrPath - The scanned URL or path
 * @returns {string|null} - Product ID or null if not found
 */
export const detectProductFromPath = (urlOrPath) => {
  if (!urlOrPath) return null;

  // Extract path from full URL if needed
  let path = urlOrPath;
  try {
    if (urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')) {
      const url = new URL(urlOrPath);
      path = url.pathname;
    }
  } catch (e) {
    // If URL parsing fails, treat as path
    path = urlOrPath;
  }

  // Improved detection: Check if path contains both GTIN and Serial
  for (const [productId, productConfig] of Object.entries(PRODUCTS)) {
    // Check for Serial
    const hasSerial = productConfig.serialNumber && path.includes(productConfig.serialNumber);

    if (hasSerial) {
      // If GTIN is present in path, it MUST match
      if (path.includes('/01/') && productConfig.gtin && !path.includes(productConfig.gtin)) {
         continue; 
      }
      return productId;
    }
  }

  return null;
};

/**
 * Get the currently active product (from localStorage or default)
 * @returns {string} - Active product ID
 */
export const getActiveProduct = () => {
  try {
    const stored = localStorage.getItem('activeProduct');
    if (stored && PRODUCTS[stored]) {
      return stored;
    }
  } catch (e) {
    console.warn('Could not access localStorage:', e);
  }
  return CURRENT_PRODUCT;
};

/**
 * Set the active product in localStorage
 * @param {string} productId - Product ID to set as active
 * @returns {boolean} - Success status
 */
export const setActiveProduct = (productId) => {
  try {
    if (!PRODUCTS[productId]) {
      console.warn(`Product '${productId}' not found`);
      return false;
    }
    localStorage.setItem('activeProduct', productId);
    return true;
  } catch (e) {
    console.warn('Could not set active product:', e);
    return false;
  }
};

/**
 * Clear active product from localStorage
 */
export const clearActiveProduct = () => {
  try {
    localStorage.removeItem('activeProduct');
  } catch (e) {
    console.warn('Could not clear active product:', e);
  }
};

/**
 * Get product by serial number (checks all QR codes)
 * @param {string} serialNumber - Serial number to search for
 * @returns {object|null} - Product configuration with current status or null
 */
export const getProductBySerialNumber = (serialNumber) => {
  for (const [key, product] of Object.entries(PRODUCTS)) {
    if (product.serialNumber === serialNumber) {
      return { 
        ...product, 
        productId: key
      };
    }
  }
  return null;
};

/**
 * Get verification status from URL hash
 * @param {string} hash - URL hash fragment (e.g., '#suspected', '#expired')
 * @returns {string} - Verification status ('verified', 'suspected', 'expired')
 */
export const getVerificationStatusFromHash = (hash) => {
  if (!hash) return 'verified';
  
  // Remove the # symbol if present
  const cleanHash = hash.replace('#', '').toLowerCase();
  
  if (cleanHash === 'suspected') return 'suspected';
  if (cleanHash === 'expired') return 'expired';
  
  return 'verified';
};

/**
 * Get verification status from serial number (deprecated - use getVerificationStatusFromHash)
 * @param {string} serialNumber - Serial number to check
 * @returns {string} - Verification status ('verified', 'suspected', 'expired')
 */
export const getVerificationStatus = (serialNumber) => {
  const product = getProductBySerialNumber(serialNumber);
  if (!product) return 'expired';
  return 'verified';
};

/**
 * Get verification state configuration
 * @param {string} status - Verification status ('verified', 'suspected', 'expired')
 * @returns {object} - Verification state configuration
 */
export const getVerificationState = (status) => {
  return productConfig.verificationStates[status] || productConfig.verificationStates.verified;
};

export const urlConfig = {
  BASE_URL,
  BASE_PATH,
  COMMON_PATH,
  ROUTES,
  CURRENT_PRODUCT,
  PRODUCTS,
  getProductPath,
  getCurrentProduct,
  getProductDataFolder,
  getRoutePath,
  getFullUrl,
  detectProductFromPath,
  getActiveProduct,
  setActiveProduct,
  clearActiveProduct,
  getProductBySerialNumber,
  getVerificationStatus,
  getVerificationStatusFromHash,
  getVerificationState
};

export default urlConfig;
